import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Target, Award, Shield, Activity, Percent, Timer, TrendingUp, FileDown, Camera, Share2, Copy, Trash2, Link as LinkIcon, Calendar, GitCompare, Star, ArrowRightLeft, Sparkles, ClipboardCheck, Plus, HeartPulse } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";
import EvaluationDetailModal from "@/components/app/EvaluationDetailModal";
import InjuryTimeline from "@/components/app/InjuryTimeline";

export default function PlayerProfile() {
  const { playerId } = useParams();
  const { user: authUser } = useAuth();
  const [data, setData] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Season comparison state
  const [seasons, setSeasons] = useState([]); // [{season, jornadas}]
  const [season, setSeason] = useState("");   // "" = todas las temporadas
  const [compareOpen, setCompareOpen] = useState(false);
  const [seasonA, setSeasonA] = useState("");
  const [seasonB, setSeasonB] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Highlights (top matches) + transfer history
  const [highlights, setHighlights] = useState([]);
  const [transfers, setTransfers] = useState([]);

  // Individual evaluations (from the independent Evaluación module)
  const [evaluations, setEvaluations] = useState([]);
  const [viewingEval, setViewingEval] = useState(null);

  // Injury history (module Lesiones)
  const [injuries, setInjuries] = useState([]);

  const load = async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const q = opts.season ? `?season=${encodeURIComponent(opts.season)}` : "";
      const hq = opts.season ? `&season=${encodeURIComponent(opts.season)}` : "";
      const [analytics, att, pl, seas, hi, tr, ev, inj] = await Promise.all([
        api.get(`/analytics/player/${playerId}${q}`),
        api.get(`/attendance/player/${playerId}/summary`).catch(() => ({ data: null })),
        api.get(`/players/${playerId}`).catch(() => ({ data: null })),
        api.get(`/analytics/player/${playerId}/seasons`).catch(() => ({ data: { seasons: [] } })),
        api.get(`/players/${playerId}/highlights?limit=3${hq}`).catch(() => ({ data: { highlights: [] } })),
        api.get(`/players/${playerId}/transfers`).catch(() => ({ data: { transfers: [] } })),
        api.get(`/evaluations?player_id=${playerId}`).catch(() => ({ data: [] })),
        api.get(`/injuries?player_id=${playerId}`).catch(() => ({ data: [] })),
      ]);
      setData(analytics.data);
      setAttendance(att.data);
      setPlayer(pl.data || analytics.data?.player);
      setSeasons(seas.data?.seasons || []);
      setHighlights(hi.data?.highlights || []);
      setTransfers(tr.data?.transfers || []);
      setEvaluations(ev.data || []);
      setInjuries(inj.data || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) setError("No tienes acceso a este jugador.");
      else if (status === 404) setError("Jugador no encontrado.");
      else {
        const msg = formatApiError(e.response?.data?.detail) || "Error al cargar el perfil";
        setError(msg); toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load({ season }); }, [playerId, season]);

  // Auto-select two seasons on first open of compare panel
  useEffect(() => {
    if (compareOpen && seasons.length && !seasonA && !seasonB) {
      setSeasonA(seasons[0]?.season || "");
      setSeasonB(seasons[1]?.season || seasons[0]?.season || "");
    }
  }, [compareOpen, seasons]);

  useEffect(() => {
    if (!compareOpen || !seasonA || !seasonB) return;
    let cancelled = false;
    (async () => {
      setCompareLoading(true);
      try {
        const [a, b] = await Promise.all([
          api.get(`/analytics/player/${playerId}?season=${encodeURIComponent(seasonA)}`),
          api.get(`/analytics/player/${playerId}?season=${encodeURIComponent(seasonB)}`),
        ]);
        if (!cancelled) { setDataA(a.data); setDataB(b.data); }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error al comparar");
      } finally { if (!cancelled) setCompareLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [compareOpen, seasonA, seasonB, playerId]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/players/${playerId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto actualizada");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!confirm("¿Eliminar la foto del jugador?")) return;
    try {
      await api.delete(`/players/${playerId}/photo`);
      toast.success("Foto eliminada");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generatePublicLink = async () => {
    try {
      await api.post(`/players/${playerId}/public-link`);
      toast.success("Enlace público generado");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revokePublicLink = async () => {
    if (!confirm("¿Revocar el enlace público? Las familias ya no podrán acceder.")) return;
    try {
      await api.delete(`/players/${playerId}/public-link`);
      toast.success("Enlace revocado");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteEvaluation = async (evalId) => {
    if (!confirm("¿Eliminar esta evaluación?")) return;
    try {
      await api.delete(`/evaluations/${evalId}`);
      toast.success("Evaluación eliminada");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-muted-foreground text-sm">Cargando…</div>;
  if (error) return (
    <div className="max-w-lg mx-auto text-center py-20">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 link-underline">
        <ArrowLeft size={14} /> Volver
      </Link>
      <div className="border border-destructive p-8">
        <div className="overline text-destructive">Acceso denegado</div>
        <div className="font-heading font-black text-3xl uppercase tracking-tighter mt-2">{error}</div>
      </div>
    </div>
  );
  if (!data) return null;

  const { totals, indicator_averages, timeline, template, coefficients } = data;
  const isPortero = data.player.position === "POR";
  const photoUrl = player?.photo_url ? `${API.replace("/api", "")}${player.photo_url}` : null;
  const publicUrl = player?.public_token ? `${window.location.origin}/p/${player.public_token}` : null;

  const radarData = (template?.blocks || []).map((b) => {
    const vals = b.indicators.map(i => indicator_averages[i.id]).filter(v => v !== undefined);
    const avg = vals.length ? vals.reduce((a, c) => a + c, 0) / vals.length : 0;
    return { block: b.name.length > 22 ? b.name.slice(0, 22) + "…" : b.name, value: Number(avg.toFixed(2)) };
  });

  const timelineData = timeline.map(t => ({
    date: t.date.slice(5),
    "Rating medio": Number(t.avg_rating.toFixed(2)),
    Goles: t.gols,
  }));

  return (
    <div>
      <Link to={`/team/${data.player.team_id}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4 link-underline">
        <ArrowLeft size={14} /> Volver al equipo
      </Link>

      <div className="flex justify-end mb-4 gap-2 flex-wrap items-center">
        {/* Season filter */}
        <div className="inline-flex items-center gap-2 border border-border h-10 px-3">
          <Calendar size={13} className="text-muted-foreground" />
          <label className="overline text-[10px]">Temporada</label>
          <select
            data-testid="player-season-selector"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="bg-transparent border-none text-xs font-mono focus:outline-none min-w-[110px]"
          >
            <option value="">Todas ({seasons.reduce((a, s) => a + s.jornadas, 0)} jornadas)</option>
            {seasons.map((s) => (
              <option key={s.season} value={s.season}>{s.season} ({s.jornadas})</option>
            ))}
          </select>
        </div>
        <button
          data-testid="toggle-compare-seasons-btn"
          onClick={() => setCompareOpen((v) => !v)}
          className={`inline-flex items-center gap-2 px-4 h-10 border text-xs uppercase font-heading tracking-wider transition-colors ${compareOpen ? "border-primary text-primary bg-primary/10" : "border-border hover:border-primary hover:text-primary"}`}
        >
          <GitCompare size={14} /> Comparar temporadas
        </button>
        <button
          data-testid="download-pdf-btn"
          onClick={async () => {
            const token = localStorage.getItem("access_token");
            const seasonQ = season ? `?season=${encodeURIComponent(season)}` : "";
            const resp = await fetch(`${API}/reports/player/${data.player.id}/pdf${seasonQ}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!resp.ok) { toast.error("No se pudo generar el PDF"); return; }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `informe-${data.player.name.replace(/ /g, "-").toLowerCase()}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Informe descargado");
          }}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border text-xs uppercase font-heading tracking-wider hover:border-primary hover:text-primary transition-colors"
        >
          <FileDown size={14} /> Descargar informe PDF
        </button>
        <button
          data-testid="download-eval-pdf-btn"
          onClick={async () => {
            try {
              toast.loading("Generando informe de evaluación…", { id: "evpdf" });
              const resp = await api.get(`/players/${playerId}/evaluation-report.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(resp.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Informe_${data.player.name.replace(/ /g, "_")}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success("Informe de evaluación descargado", { id: "evpdf" });
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: "evpdf" }); }
          }}
          className="inline-flex items-center gap-2 px-4 h-10 border border-primary text-primary text-xs uppercase font-heading tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          <ClipboardCheck size={14} /> PDF Evaluación
        </button>
        <button
          data-testid="send-eval-pdf-btn"
          onClick={async () => {
            if (!window.confirm("¿Enviar el informe de evaluación por email a los tutores registrados?")) return;
            try {
              toast.loading("Enviando informe a las familias…", { id: "evsend" });
              const { data: r } = await api.post(`/players/${playerId}/evaluation-report/send`);
              toast.success(`Informe enviado a ${r.sent}/${r.total} tutor(es)`, { id: "evsend" });
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: "evsend" }); }
          }}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border text-xs uppercase font-heading tracking-wider hover:border-primary hover:text-primary transition-colors"
        >
          <Share2 size={14} /> Enviar a familias
        </button>
      </div>

      {/* Season Comparison Panel */}
      {compareOpen && (
        <SeasonComparePanel
          seasons={seasons}
          seasonA={seasonA} setSeasonA={setSeasonA}
          seasonB={seasonB} setSeasonB={setSeasonB}
          dataA={dataA} dataB={dataB}
          loading={compareLoading}
          onClose={() => setCompareOpen(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 border border-border p-6">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="relative w-28 h-28 border border-border flex items-center justify-center overflow-hidden group">
              {photoUrl ? (
                <img src={photoUrl} alt={data.player.name} className="w-full h-full object-cover" data-testid="player-photo" />
              ) : (
                <div className="font-heading font-black text-5xl">{data.player.jersey_number ?? "—"}</div>
              )}
              <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 cursor-pointer transition-opacity" data-testid="player-photo-uploader-label">
                <Camera size={18} className="text-white" />
                <span className="text-[10px] uppercase font-heading tracking-wider text-white">{uploading ? "Subiendo…" : "Cambiar"}</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="player-photo-input"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                />
              </label>
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline">Jugador</div>
              <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase mt-1 leading-none">{data.player.name}</h1>
              <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
                <span className="px-2 py-1 border border-border font-mono">{data.player.position || "—"}</span>
                <span className="px-2 py-1 border border-border font-mono">Pie {data.player.dominant_foot || "—"}</span>
                {data.player.birthdate && <span className="px-2 py-1 border border-border font-mono">Nac. {data.player.birthdate}</span>}
                {photoUrl && (
                  <button
                    data-testid="remove-photo-btn"
                    onClick={removePhoto}
                    className="px-2 py-1 border border-border hover:border-red-400 hover:text-red-400 font-mono inline-flex items-center gap-1"
                  ><Trash2 size={10} /> Quitar foto</button>
                )}
              </div>
              {data.racha_semanas >= 2 && (
                <div data-testid="player-racha-badge" className="mt-3 inline-flex items-center gap-2 border border-amber-500/60 bg-amber-500/10 text-amber-500 px-3 py-1.5 text-xs uppercase font-heading font-black tracking-wider" title="Semanas seguidas jugando partido">
                  🔥 En racha: {data.racha_semanas} semanas seguidas jugando
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="border border-border p-6">
          <div className="overline">Rendimiento acumulado</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Metric icon={Target} label="Goles" value={totals.gols} />
            <Metric icon={Award} label="Asistencias" value={totals.asistencias} />
            <Metric icon={Trophy} label="Convocatorias" value={totals.convocatorias} />
            <Metric icon={Timer} label="Minutos" value={totals.minutos} />
            <Metric label="T. amarillas" value={totals.targetes_grogues} />
            <Metric label="T. rojas" value={totals.targetes_roges} />
          </div>
        </div>
      </div>

      {/* Public link */}
      <div className="border border-border mb-8 p-4 md:p-5" data-testid="public-link-section">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 border ${publicUrl ? "border-primary text-primary bg-primary/10" : "border-border"} flex items-center justify-center shrink-0`}>
              <LinkIcon size={16} />
            </div>
            <div className="min-w-0">
              <div className="overline">Enlace público del jugador</div>
              <div className="font-heading font-bold text-sm uppercase tracking-tight mt-1">
                {publicUrl ? "Enlace activo · comparte con la familia" : "Sin enlace público"}
              </div>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                {publicUrl
                  ? "Cualquiera con este enlace verá el perfil sin necesidad de contraseña. No expone datos personales de padres."
                  : "Genera un enlace para que la familia pueda ver las estadísticas del jugador y compartirlo por WhatsApp."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {publicUrl ? (
              <>
                <button
                  data-testid="copy-public-link-btn"
                  onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); }}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider"
                ><Copy size={12} /> Copiar</button>
                <a
                  data-testid="share-whatsapp-btn"
                  href={`https://wa.me/?text=${encodeURIComponent(`Estadísticas de ${data.player.name}: ${publicUrl}`)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider"
                ><Share2 size={12} /> WhatsApp</a>
                <button
                  data-testid="revoke-public-link-btn"
                  onClick={revokePublicLink}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-border hover:border-red-400 hover:text-red-400 text-xs uppercase font-heading tracking-wider"
                ><Trash2 size={12} /> Revocar</button>
              </>
            ) : (
              <button
                data-testid="generate-public-link-btn"
                onClick={generatePublicLink}
                className="inline-flex items-center gap-2 h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider"
              ><LinkIcon size={12} /> Generar enlace público</button>
            )}
          </div>
        </div>
        {publicUrl && (
          <div className="mt-3 font-mono text-[11px] bg-black/30 border border-border p-2 truncate" data-testid="public-link-url">
            {publicUrl}
          </div>
        )}
      </div>

      {/* Coefficients / end-of-season report */}
      <div className="border border-border mb-8">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <TrendingUp size={14} className="text-primary" />
          <div className="overline">Coeficientes de rendimiento — Informe de temporada</div>
        </div>
        <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
          <Coef icon={Timer} label="Minutos por partido" value={coefficients.minutos_por_partido} suffix="'" />
          <Coef icon={Target} label="Goles / 90'" value={coefficients.goles_por_90} />
          <Coef icon={Award} label="Asist. / 90'" value={coefficients.asistencias_por_90} />
          <Coef icon={Activity} label="G+A / 90'" value={coefficients.participaciones_gol_por_90} accent />
          <Coef icon={Percent} label="Convocatorias" value={coefficients.porcentaje_convocatorias} suffix="%" />
          <Coef icon={TrendingUp} label="Rating medio" value={coefficients.rating_medio} suffix=" / 5" />
          {isPortero && (
            <>
              <Coef icon={Shield} label="Porterías a cero" value={coefficients.porterias_cero} accent />
              <Coef icon={Percent} label="% Porterías cero" value={coefficients.porcentaje_porterias_cero} suffix="%" />
              <Coef icon={Award} label="Paradas / partido" value={coefficients.ratio_paradas_por_partido} />
            </>
          )}
          {attendance && attendance.total_sessions > 0 && (
            <Coef icon={Percent} label="Asistencia entrenos" value={attendance.attendance_rate} suffix="%" accent={attendance.attendance_rate >= 80} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* HIGHLIGHTS panel */}
        <div className="border border-border" data-testid="highlights-panel">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-yellow-400" />
              <span className="overline">Mejores partidos</span>
            </div>
            <span className="text-[10px] font-mono text-muted-foreground">Auto-seleccionados</span>
          </div>
          {highlights.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Sin partidos disputados aún.</div>
          ) : (
            <div>
              {highlights.map((h, i) => (
                <div key={h.jornada_id || i} className="p-4 border-b border-border/40 last:border-b-0 flex items-center gap-4" data-testid={`highlight-${i}`}>
                  <div className={`w-9 h-9 flex items-center justify-center font-heading font-black text-sm ${i === 0 ? "bg-yellow-400 text-black" : i === 1 ? "bg-slate-300 text-black" : "bg-orange-500 text-white"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-sm uppercase truncate">{h.opponent || "—"}</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${h.result === "V" ? "border-emerald-400 text-emerald-400" : h.result === "E" ? "border-yellow-400 text-yellow-400" : "border-red-400 text-red-400"}`}>{h.result} {h.team_score}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{h.home_away === "casa" ? "🏠" : h.home_away === "fora" ? "✈️" : ""}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{h.date} · {h.minutos}&#39; jugados</div>
                    <div className="flex items-center gap-3 mt-2 text-xs font-mono">
                      {h.gols > 0 && <span className="text-primary font-bold">{h.gols}⚽</span>}
                      {h.asistencias > 0 && <span className="text-blue-400 font-bold">{h.asistencias}🅰</span>}
                      {h.rating > 0 && <span className="text-yellow-400"><Star size={10} className="inline mb-0.5" /> {h.rating}/5</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="overline text-[9px]">Score</div>
                    <div className="metric-number text-lg text-primary">{h.score}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* TRANSFER HISTORY panel */}
        <div className="border border-border" data-testid="transfers-panel">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <ArrowRightLeft size={13} className="text-blue-400" />
            <span className="overline">Historial de traslados</span>
          </div>
          {transfers.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Sin cambios de equipo registrados.</div>
          ) : (
            <div>
              {transfers.map((t) => (
                <div key={t.id} className="p-4 border-b border-border/40 last:border-b-0" data-testid={`transfer-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground border border-border px-2 py-0.5">{t.from_team_name || "—"}</span>
                    <ArrowRightLeft size={12} className="text-blue-400" />
                    <span className="text-xs font-mono text-primary font-bold border border-primary px-2 py-0.5">{t.to_team_name}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2 font-mono flex items-center gap-3">
                    <span>{t.date}</span>
                    {t.to_season && <span className="border border-border px-1.5">Temp. {t.to_season}</span>}
                    {t.actor_email && <span className="opacity-60">· por {t.actor_email}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* INJURY HISTORY panel */}
      {injuries.length > 0 && (
        <div className="border border-border mb-8" data-testid="player-injuries-panel">
          <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <HeartPulse size={13} className="text-primary" />
              <span className="overline">Historial de lesiones</span>
              <span className="text-[10px] font-mono text-muted-foreground">· {injuries.length} registro{injuries.length === 1 ? "" : "s"}</span>
              {injuries.some(i => i.status === "active") && (
                <span className="ml-2 px-1.5 py-0.5 border border-destructive text-destructive text-[9px] font-heading tracking-widest animate-pulse">
                  {injuries.filter(i => i.status === "active").length} ACTIVA(S)
                </span>
              )}
            </div>
            <Link to="/injuries" className="text-[10px] font-heading tracking-widest text-muted-foreground hover:text-primary uppercase">Ver módulo →</Link>
          </div>
          <div className="relative pl-0 pr-0 py-0">
            <InjuryTimeline injuries={injuries} mode="staff" testid="player-injuries-timeline" />
          </div>
        </div>
      )}

      {/* EVALUATIONS panel (independent module Evaluación) */}
      <div className="border border-border mb-8" data-testid="player-evaluations-panel">
        <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={13} className="text-primary" />
            <span className="overline">Evaluaciones individuales</span>
            <span className="text-[10px] font-mono text-muted-foreground">· {evaluations.length} registro{evaluations.length === 1 ? "" : "s"}</span>
          </div>
          <Link
            to={`/evaluations?player=${playerId}`}
            data-testid="go-to-evaluations-btn"
            className="inline-flex items-center gap-2 h-8 px-3 border border-border text-[10px] uppercase font-heading tracking-wider hover:border-primary hover:text-primary"
          >
            <Plus size={12} /> Ir a Evaluación
          </Link>
        </div>
        {evaluations.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            Sin evaluaciones individuales registradas.
            <div className="text-[11px] mt-1 opacity-70">Crea una desde el módulo <Link to={`/evaluations?player=${playerId}`} className="text-primary hover:underline">Evaluación</Link>.</div>
          </div>
        ) : (
          <div>
            {evaluations.slice(0, 8).map((ev) => {
              const nums = (ev.ratings || [])
                .filter(r => r.rating !== undefined && r.rating !== null)
                .map(r => Number(r.rating));
              const avg = nums.length ? (nums.reduce((a, b) => a + b, 0) / nums.length) : null;
              // Mirrors backend rules: super_admin & club_admin can always delete; coordinator/DD too;
              // coach/delegado only if they authored it.
              const role = authUser?.role;
              const isAdminRole = ["super_admin", "club_admin", "coordinator", "director_deportivo"].includes(role);
              const isTeamStaff = ["coach", "delegado"].includes(role);
              const canDelete = isAdminRole || (isTeamStaff && ev.author_id === authUser?.id);
              return (
                <div key={ev.id} className="p-4 border-b border-border/40 last:border-b-0 flex items-center gap-4" data-testid={`player-eval-row-${ev.id}`}>
                  <div className="w-10 h-10 border border-border flex items-center justify-center shrink-0">
                    <ClipboardCheck size={14} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-sm uppercase truncate">{ev.date}</span>
                      {avg !== null && <span className="text-[10px] font-mono px-1.5 py-0.5 border border-primary text-primary">Media {avg.toFixed(1)}/5</span>}
                      <span className="text-[10px] font-mono text-muted-foreground">· {nums.length} ind.</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Por {ev.author_name}
                      {ev.objectives && <span className="ml-2 opacity-80">· {ev.objectives.slice(0, 60)}{ev.objectives.length > 60 ? "…" : ""}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`view-player-eval-${ev.id}`}
                      onClick={() => setViewingEval(ev)}
                      className="text-xs uppercase font-heading tracking-wider text-primary hover:underline"
                    >Ver</button>
                    {canDelete && (
                      <button
                        data-testid={`del-player-eval-${ev.id}`}
                        onClick={() => deleteEvaluation(ev.id)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Eliminar (si tienes permisos)"
                      ><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              );
            })}
            {evaluations.length > 8 && (
              <div className="p-3 text-center text-[11px] text-muted-foreground border-t border-border">
                Mostrando 8 de {evaluations.length}. <Link to={`/evaluations?player=${playerId}`} className="text-primary hover:underline">Ver todas</Link>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="border border-border">
          <div className="p-4 border-b border-border overline">Perfil por bloque (radar)</div>
          <div className="p-4 h-80">
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis dataKey="block" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.6)' }} />
                  <PolarRadiusAxis domain={[1, 5]} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
                  <Radar name="Nivel" dataKey="value" stroke="#e11d2e" fill="#e11d2e" fillOpacity={0.35} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: 4, fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin evaluaciones.</div>}
          </div>
        </div>
        <div className="border border-border">
          <div className="p-4 border-b border-border overline">Evolución en el tiempo</div>
          <div className="p-4 h-80">
            {timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="Rating medio" stroke="#e11d2e" strokeWidth={2} dot={{ r: 3, fill: '#e11d2e' }} />
                  <Line type="monotone" dataKey="Goles" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin datos temporales.</div>}
          </div>
        </div>
      </div>

      <div className="border border-border">
        <div className="p-4 border-b border-border overline">Detalle de indicadores</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {(template?.blocks || []).map((b) => (
            <div key={b.id} className="border-r border-b border-border p-4">
              <div className="font-heading font-bold uppercase tracking-tight text-sm mb-3">{b.name}</div>
              <div className="space-y-2">
                {b.indicators.map((ind) => {
                  const avg = indicator_averages[ind.id];
                  const pct = avg !== undefined ? Math.round((avg / 5) * 100) : 0;
                  return (
                    <div key={ind.id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="truncate mr-2">{ind.name}</span>
                        <span className="font-mono text-primary">{avg !== undefined ? `${avg.toFixed(2)}/5` : "—"}</span>
                      </div>
                      <div className="h-1 bg-border">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {viewingEval && (
        <EvaluationDetailModal
          evaluation={viewingEval}
          playerName={data.player.name}
          teamName={""}
          playerPosition={data.player.position}
          onClose={() => setViewingEval(null)}
        />
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="border border-border p-3">
      <div className="flex items-center gap-1 overline">{Icon && <Icon size={11} />}{label}</div>
      <div className="metric-number text-3xl mt-1">{value ?? 0}</div>
    </div>
  );
}

function Coef({ icon: Icon, label, value, suffix = "", accent }) {
  const display = value === null || value === undefined ? "—" : value;
  return (
    <div className="p-4">
      <div className="flex items-center gap-1 overline">{Icon && <Icon size={11} />}{label}</div>
      <div className={`metric-number text-3xl mt-2 ${accent ? "text-primary" : ""}`}>
        {display}{suffix && display !== "—" ? <span className="text-xs opacity-60">{suffix}</span> : null}
      </div>
    </div>
  );
}

// ---- Season comparison panel ----
function SeasonComparePanel({ seasons, seasonA, setSeasonA, seasonB, setSeasonB, dataA, dataB, loading, onClose }) {
  const options = seasons.length ? seasons : [];
  // Build radar data from both seasons' indicator_averages using union of blocks
  const buildRadar = () => {
    const blocksSource = dataA?.template?.blocks || dataB?.template?.blocks || [];
    return blocksSource.map((b) => {
      const avgA = avgForBlock(b, dataA?.indicator_averages);
      const avgB = avgForBlock(b, dataB?.indicator_averages);
      return {
        block: b.name.length > 20 ? b.name.slice(0, 20) + "…" : b.name,
        [seasonA || "A"]: Number(avgA.toFixed(2)),
        [seasonB || "B"]: Number(avgB.toFixed(2)),
      };
    });
  };
  const radar = (dataA && dataB) ? buildRadar() : [];

  const rows = [
    { label: "Partidos jugados", key: (d) => d.coefficients?.partidos_jugados },
    { label: "Minutos", key: (d) => d.totals?.minutos },
    { label: "Minutos / partido", key: (d) => d.coefficients?.minutos_por_partido, suffix: "'" },
    { label: "Goles", key: (d) => d.totals?.gols, accent: true },
    { label: "Asistencias", key: (d) => d.totals?.asistencias },
    { label: "G / 90'", key: (d) => d.coefficients?.goles_por_90 },
    { label: "A / 90'", key: (d) => d.coefficients?.asistencias_por_90 },
    { label: "G+A / 90'", key: (d) => d.coefficients?.participaciones_gol_por_90, accent: true },
    { label: "Convocatorias", key: (d) => d.totals?.convocatorias },
    { label: "% Convocatorias", key: (d) => d.coefficients?.porcentaje_convocatorias, suffix: "%" },
    { label: "Rating medio", key: (d) => d.coefficients?.rating_medio, suffix: " / 5" },
    { label: "T. amarillas", key: (d) => d.totals?.targetes_grogues },
    { label: "T. rojas", key: (d) => d.totals?.targetes_roges },
  ];

  return (
    <div className="border border-primary/60 bg-primary/5 mb-6" data-testid="season-compare-panel">
      <div className="p-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <GitCompare size={14} className="text-primary" />
          <div className="overline">Comparativa de temporadas</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <SeasonSelect label="A" tid="compare-season-a" value={seasonA} onChange={setSeasonA} options={options} />
          <span className="text-muted-foreground text-sm">vs</span>
          <SeasonSelect label="B" tid="compare-season-b" value={seasonB} onChange={setSeasonB} options={options} />
          {seasonA && seasonB && seasonA !== seasonB && (
            <button
              data-testid="download-compare-pdf-btn"
              onClick={async () => {
                const token = localStorage.getItem("access_token");
                const url = `${API}/reports/player/${dataA?.player?.id || dataB?.player?.id}/pdf?season=${encodeURIComponent(seasonB)}&compare_with=${encodeURIComponent(seasonA)}`;
                try {
                  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                  if (!resp.ok) throw new Error("PDF error");
                  const blob = await resp.blob();
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `comparativa-${seasonA}-vs-${seasonB}.pdf`;
                  link.click();
                  URL.revokeObjectURL(link.href);
                } catch (e) {
                  toast.error("No se pudo generar el PDF comparativo");
                }
              }}
              className="inline-flex items-center gap-2 h-8 px-3 bg-primary text-primary-foreground text-[10px] uppercase font-heading tracking-wider hover:bg-primary/80"
              title="Descargar PDF comparativo con portada visual"
            >
              <FileDown size={12} /> PDF Comparativa
            </button>
          )}
          <button
            data-testid="close-compare-panel-btn"
            onClick={onClose}
            className="h-8 px-3 border border-border text-[10px] uppercase font-heading tracking-wider hover:border-red-400 hover:text-red-400"
          >Cerrar</button>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="compare-loading">Calculando comparativa…</div>
      )}

      {!loading && dataA && dataB && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {/* Metrics table */}
          <div className="lg:col-span-2 border-r border-border">
            <div className="grid grid-cols-3 border-b border-border">
              <div className="p-3 overline text-[10px]">Métrica</div>
              <div className="p-3 overline text-[10px] text-primary text-right" data-testid="compare-header-a">{seasonA}</div>
              <div className="p-3 overline text-[10px] text-blue-300 text-right" data-testid="compare-header-b">{seasonB}</div>
            </div>
            {rows.map((r) => {
              const va = r.key(dataA);
              const vb = r.key(dataB);
              const na = va == null ? null : Number(va);
              const nb = vb == null ? null : Number(vb);
              // Diff = B - A: positive means B improved over A (green), negative means B declined (red).
              const diff = (na != null && nb != null) ? nb - na : null;
              return (
                <div key={r.label} className="grid grid-cols-3 border-b border-border/40 text-sm" data-testid={`compare-row-${r.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <div className="p-3 text-muted-foreground text-xs">{r.label}</div>
                  <div className={`p-3 font-mono text-right ${r.accent ? "text-primary font-bold" : ""}`}>
                    {va == null ? "—" : va}{r.suffix && va != null ? <span className="text-xs opacity-60">{r.suffix}</span> : null}
                  </div>
                  <div className={`p-3 font-mono text-right ${r.accent ? "text-blue-300 font-bold" : ""}`}>
                    <span>{vb == null ? "—" : vb}{r.suffix && vb != null ? <span className="text-xs opacity-60">{r.suffix}</span> : null}</span>
                    {diff != null && diff !== 0 && (
                      <span className={`ml-2 text-[10px] font-mono ${diff > 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {diff > 0 ? "+" : ""}{Number(diff.toFixed(2))}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Radar chart */}
          <div className="p-4">
            <div className="overline mb-2">Radar por bloque</div>
            <div className="h-72" data-testid="compare-radar">
              {radar.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radar}>
                    <PolarGrid stroke="rgba(255,255,255,0.1)" />
                    <PolarAngleAxis dataKey="block" tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.6)' }} />
                    <PolarRadiusAxis domain={[1, 5]} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }} />
                    <Radar name={seasonA} dataKey={seasonA || "A"} stroke="#e11d2e" fill="#e11d2e" fillOpacity={0.3} />
                    <Radar name={seasonB} dataKey={seasonB || "B"} stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.2} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: 4, fontSize: 11 }} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Sin datos suficientes.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && (!dataA || !dataB) && (
        <div className="p-6 text-center text-muted-foreground text-sm">Selecciona dos temporadas para comparar.</div>
      )}
    </div>
  );
}

function avgForBlock(block, averages = {}) {
  const vals = (block.indicators || []).map(i => averages[i.id]).filter(v => v !== undefined);
  return vals.length ? vals.reduce((a, c) => a + c, 0) / vals.length : 0;
}

function SeasonSelect({ label, tid, value, onChange, options }) {
  return (
    <label className="inline-flex items-center gap-2 border border-border h-8 px-2">
      <span className="overline text-[9px]">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}
              className="bg-transparent border-none text-xs font-mono focus:outline-none">
        <option value="">—</option>
        {options.map(o => <option key={o.season} value={o.season}>{o.season}</option>)}
      </select>
    </label>
  );
}
